import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { customAxiosInstanceMutator } from '../../../services/custom-axios-instance';
import CreditDirectorReportsModal from '../loan-processing/CreditDirectorReportsModal';
import { DirectorReportView } from '../loan-processing/creditDirectorReportsTypes';
import { formatDate } from '../../../utils/formatters';

interface CreditReportRecord {
  directorIdNumber?: string;
  directorName?: string;
  recommendation?: string;
  riskLevel?: string;
  modelProbability?: number;
  createdDate?: string;
  report?: Record<string, unknown>;
  scoringEngineResult?: Record<string, unknown>;
  failureReason?: string | null;
}

interface CreditReportHistoryPage {
  content: CreditReportRecord[];
  totalElements: number;
  totalPages: number;
  size: number;
  page: number;
}

const CreditReportHistory = () => {
  const [searchName, setSearchName] = useState('');
  const [searchIdNumber, setSearchIdNumber] = useState('');
  const [reports, setReports] = useState<CreditReportRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState<CreditReportRecord | null>(null);

  const fetchReports = async (pageNum = 0, size = 10, name = '', idNumber = '') => {
    setIsLoading(true);
    try {
      const params: Record<string, any> = { page: pageNum, size };
      if (name.trim()) params.name = name.trim();
      if (idNumber.trim()) params.idNumber = idNumber.trim();

      const response = await customAxiosInstanceMutator<CreditReportHistoryPage>({
        url: '/loan/api/v1/credit-report-history',
        method: 'GET',
        params
      });

      setReports(response.content || []);
      setTotalRows(response.totalElements || 0);
    } catch (err: any) {
      console.error('Error fetching credit report history:', err);
      toast.error(err?.response?.data?.message || 'Failed to fetch credit report history');
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(page, perPage, searchName, searchIdNumber);
  }, [page, perPage]);

  const handleSearch = () => {
    setPage(0);
    fetchReports(0, perPage, searchName, searchIdNumber);
  };

  const handleViewReport = (report: CreditReportRecord) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  const getReportViews = (): DirectorReportView[] => {
    if (!selectedReport) return [];
    return [{
      id: selectedReport.directorIdNumber || 'unknown',
      label: selectedReport.directorName || selectedReport.directorIdNumber || 'N/A',
      status: selectedReport.failureReason ? 'FAILED' : 'SUCCESS',
      creditScore: (selectedReport.report as any)?.consumerScoring?.finalScore ?? null,
      xdsReport: selectedReport.report || {},
      scoringEngineResult: selectedReport.scoringEngineResult || {}
    }];
  };

  const getRiskBand = (report: CreditReportRecord): string => {
    const scoring = (report.scoringEngineResult as any)?.scoring;
    return scoring?.risk_band || report.riskLevel || 'N/A';
  };

  const getModelProbability = (report: CreditReportRecord): number | null => {
    const scoring = (report.scoringEngineResult as any)?.scoring;
    return scoring?.model_probability ?? report.modelProbability ?? null;
  };

  const columns = [
    {
      name: 'Name',
      selector: (row: CreditReportRecord) => row.directorName || '',
      sortable: true,
      cell: (row: CreditReportRecord) => (
        <div>
          <span className="fw-semibold">{row.directorName || 'N/A'}</span>
          <br />
          <small className="text-muted">{row.directorIdNumber || ''}</small>
        </div>
      ),
      minWidth: '200px',
    },
    {
      name: 'Recommendation',
      selector: (row: CreditReportRecord) => row.recommendation || '',
      sortable: true,
      cell: (row: CreditReportRecord) => (
        <span className={`badge px-2 py-1 ${
          row.recommendation === 'APPROVE' || row.recommendation === 'APPROVE_WITH_CONDITIONS' ? 'bg-success' :
          row.recommendation === 'REJECT' || row.recommendation === 'DECLINE' ? 'bg-danger' : 'bg-warning text-dark'
        }`}>
          {row.recommendation || 'N/A'}
        </span>
      ),
      minWidth: '140px',
    },
    {
      name: 'Risk Band',
      selector: (row: CreditReportRecord) => getRiskBand(row),
      sortable: true,
      cell: (row: CreditReportRecord) => (
        <span className="fw-semibold">{getRiskBand(row)}</span>
      ),
      minWidth: '150px',
    },
    {
      name: 'Probability',
      selector: (row: CreditReportRecord) => getModelProbability(row) || 0,
      sortable: true,
      cell: (row: CreditReportRecord) => {
        const prob = getModelProbability(row);
        return <span className="fw-bold">{prob != null ? `${prob}%` : 'N/A'}</span>;
      },
      minWidth: '110px',
    },
    {
      name: 'Date',
      selector: (row: CreditReportRecord) => row.createdDate || '',
      sortable: true,
      cell: (row: CreditReportRecord) => (
        <span>{row.createdDate ? formatDate(row.createdDate) : 'N/A'}</span>
      ),
      minWidth: '120px',
    },
    {
      name: 'Action',
      cell: (row: CreditReportRecord) => (
        <button
          className="btn btn-sm btn-primary rounded-pill px-3"
          onClick={() => handleViewReport(row)}
        >
          <i className="fa fa-eye me-1"></i>
          View Report
        </button>
      ),
      minWidth: '140px',
    },
  ];

  return (
    <div>
      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                <li className="breadcrumb-item active" aria-current="page">Credit Report History</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          {/* Search */}
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-5">
                  <label className="form-label fw-semibold">
                    <i className="fa fa-user me-2 text-muted"></i>Search by Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter individual's name..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    <i className="fa fa-id-card me-2 text-muted"></i>Search by ID Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter ID number..."
                    value={searchIdNumber}
                    onChange={(e) => setSearchIdNumber(e.target.value.replace(/\D/g, '').slice(0, 13))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    maxLength={13}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <div className="d-flex gap-2">
                    <button className="btn btn-primary flex-grow-1" onClick={handleSearch}>
                      <i className="fa fa-search me-2"></i>Search
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setSearchName('');
                        setSearchIdNumber('');
                        setPage(0);
                        fetchReports(0, perPage, '', '');
                      }}
                    >
                      <i className="fa fa-refresh"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                data={reports}
                progressPending={isLoading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                onChangePage={(p) => setPage(p - 1)}
                onChangeRowsPerPage={(newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(0);
                }}
                highlightOnHover
                striped
                noDataComponent={
                  <div className="text-center py-5">
                    <i className="fa fa-file-text fa-3x text-muted mb-3" style={{ display: 'block' }}></i>
                    <h6 className="text-muted">No Credit Reports Found</h6>
                    <p className="text-muted small">Run a credit assessment to generate reports.</p>
                  </div>
                }
                customStyles={{
                  headRow: {
                    style: {
                      backgroundColor: '#f8f9fa',
                      borderBottom: '2px solid #dee2e6',
                      fontWeight: 700,
                    },
                  },
                  rows: {
                    style: {
                      minHeight: '60px',
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <CreditDirectorReportsModal
        show={showReportModal}
        onHide={() => setShowReportModal(false)}
        reports={getReportViews()}
        selectedIndex={0}
        onSelectIndex={() => {}}
        processedCount={selectedReport ? 1 : 0}
        successCount={selectedReport ? 1 : 0}
        failedCount={0}
        showDownloadControl={true}
        downloadEnabled={true}
      />
    </div>
  );
};

export default CreditReportHistory;
